﻿/** src/App.tsx
 * Main app entry with routing.
 * Wraps everything in LoaderProvider for global loading spinner and AuthProvider for authentication.
 */
import { Routes, Route, Navigate } from "react-router-dom";
import { createContext, useMemo, useState, useCallback, useEffect } from "react";
import { LoaderProvider } from "./pages/LoaderContext";
import { AuthProvider } from "./contexts/AuthContext";
import { ToastProvider } from "./contexts/ToastContext";

import Navigation from "./components/Navigation";
import AuthModal from "./components/AuthModal";

import UserLayout from "./layouts/UserLayout";
import AdminLayout from "./layouts/AdminLayout";

import User from "./pages/User";
import AboutEnvironment from "./pages/AboutEnvironment";
import Admin from "./pages/Admin";
import AdminAboutEnvironment from "./components/AdminAboutEnvironment";
import Profile from "./components/Profile";
import MyBookings from "./components/MyBookings";
import RoomDetail from "./components/RoomDetail";
import LandingPage from "./pages/Landing"; // landing page

/* ------------------------------------------------------------------
   Landing language / theme context
------------------------------------------------------------------- */
export type LandingLanguage = "en" | "fr";
export type ThemeMode = "light" | "dark";

export const LandingLanguageContext = createContext<{
  language: LandingLanguage;
  setLanguage: (lang: LandingLanguage) => void;
  toggleLanguage: () => void;
}>(
  {
    language: "en",
    setLanguage: () => {},
    toggleLanguage: () => {},
  }
);

export const ThemeContext = createContext<{
  theme: ThemeMode;
  toggleTheme: () => void;
}>(
  {
    theme: "dark",
    toggleTheme: () => {},
  }
);

function App() {
  const [language, setLanguage] = useState<LandingLanguage>("en");
  const [theme, setTheme] = useState<ThemeMode>("dark");
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  useEffect(() => {
    const storedTheme = localStorage.getItem("theme") as ThemeMode | null;
    if (storedTheme) {
      setTheme(storedTheme);
    }
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("theme", theme);
  }, [theme]);

  const setLanguageFn = useCallback((lang: LandingLanguage) => {
    setLanguage(lang);
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage((prev) => (prev === "en" ? "fr" : "en"));
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const languageContextValue = useMemo(
    () => ({
      language,
      setLanguage: setLanguageFn,
      toggleLanguage,
    }),
    [language, setLanguageFn, toggleLanguage]
  );

  const themeContextValue = useMemo(
    () => ({
      theme,
      toggleTheme,
    }),
    [theme, toggleTheme]
  );

  return (
    <ToastProvider>
      <AuthProvider>
        <LoaderProvider>
          <ThemeContext.Provider value={themeContextValue}>
            <LandingLanguageContext.Provider value={languageContextValue}>
              <div className="min-h-screen">
                <Navigation onOpenAuth={() => setIsAuthModalOpen(true)} />
                <main className="pt-16">
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/" element={<UserLayout />}>
                      <Route path="booking" element={<User />} />
                      <Route path="about-environment" element={<AboutEnvironment />} />
                      <Route path="rooms" element={<User />} />
                      <Route path="rooms/:roomId" element={<RoomDetail />} />
                    </Route>
                    <Route path="/admin" element={<AdminLayout />}>
                      <Route index element={<Admin />} />
                      <Route path="about-environment" element={<AdminAboutEnvironment />} />
                    </Route>
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/my-bookings" element={<MyBookings />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </main>
              </div>
              <AuthModal
                isOpen={isAuthModalOpen}
                onClose={() => setIsAuthModalOpen(false)}
              />
            </LandingLanguageContext.Provider>
          </ThemeContext.Provider>
        </LoaderProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
